/**
 * SEO-friendly slug utilities for coach public profile URLs.
 *
 * Slug format: {name}-{bio-keywords}
 * Examples:
 *   "Sarah Chen", "Former D1 serve specialist in Austin" → "sarah-chen-serve-specialist-austin"
 *   "James Brown", specialties: ["Footwork", "Match Strategy"] → "james-brown-footwork-match-strategy"
 *   "Mike Davis" (no descriptor) → "mike-davis"
 */

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'in', 'at', 'for', 'with', 'and', 'or', 'of',
  'to', 'is', 'be', 'my', 'i', 'as', 'by', 'on', 'up', 'it',
  'he', 'she', 'we', 'you', 'this', 'that', 'are', 'was', 'has',
  'had', 'but', 'not', 'so', 'do', 'did', 'can', 'its', 'from',
  'who', 'all', 'out', 'if',
]);

/** Convert any string into a URL-safe lowercase hyphenated segment */
export function toSlugPart(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')   // strip diacritics
    .replace(/[^a-z0-9\s-]/g, '')      // strip non-alphanumeric
    .trim()
    .replace(/[\s-]+/g, '-');          // collapse spaces/hyphens
}

/**
 * Extract 1–3 meaningful keyword slugs from a free-text descriptor.
 * Filters stop words and very short tokens.
 */
function extractKeywords(text: string, limit = 3): string[] {
  const words = text
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 3 && !STOP_WORDS.has(w));

  // Prefer longer, more distinctive words
  const ranked = Array.from(new Set(words)).sort((a, b) => b.length - a.length);
  return ranked.slice(0, limit);
}

/**
 * Build a slug base string from coach profile fields.
 *
 * Priority:
 *   1. Coach name (always included)
 *   2. Keywords from shortDescriptor (most distinctive signal)
 *   3. First specialty if no descriptor keywords found
 *   4. City from location (first segment before comma) if slug is still short
 */
export function buildSlugBase(
  displayName: string,
  shortDescriptor?: string | null,
  specialties?: string[] | null,
  location?: string | null,
): string {
  const parts: string[] = [];

  // 1. Name
  const namePart = toSlugPart(displayName);
  if (namePart) parts.push(namePart);

  // 2. Descriptor keywords
  if (shortDescriptor) {
    const keywords = extractKeywords(shortDescriptor, 2);
    parts.push(...keywords);
  }

  // 3. Specialty fallback
  if (parts.length === 1 && specialties?.length) {
    const specialty = toSlugPart(specialties[0]);
    if (specialty) parts.push(specialty);
  }

  // 4. City — only if slug is still short and city isn't already represented
  const combined = parts.join('-');
  if (location && combined.length < 28) {
    const city = toSlugPart(location.split(',')[0].trim());
    if (city && !combined.includes(city)) parts.push(city);
  }

  return parts.join('-').slice(0, 64);
}

/** Returns true if a route param looks like a slug (contains a hyphen) vs a Firebase UID */
export function isSlug(param: string): boolean {
  return param.includes('-');
}
